/**
 * UsedInventoryAgingCard Component
 *
 * Forecast-page collapsible card (USED vehicle department only). Same chrome
 * and table layout as InventoryAgingCard, but no per-brand breakdown — used
 * inventory shows a single totals row with the 60-day industry-standard
 * threshold instead of 90.
 */

import React from 'react';
import { ChevronDownIcon, CubeIcon } from '@heroicons/react/24/outline';
import { UsedInventoryAgingResponse } from '../../services/api';

interface UsedInventoryAgingCardProps {
  data: UsedInventoryAgingResponse | null;
  loading: boolean;
  error: string | null;
  locationName: string;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
}

function formatNumber(n: number): string {
  return new Intl.NumberFormat('en-US').format(n);
}

function formatPct(n: number): string {
  return `${n.toFixed(1)}%`;
}

function formatDate(yyyymmdd?: string): string {
  if (!yyyymmdd) return '';
  const [y, m, d] = yyyymmdd.split('-').map((s) => parseInt(s, 10));
  if (!y || !m || !d) return yyyymmdd;
  return new Date(y, m - 1, d).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function ymmDescription(year: number | null, make: string | null, model: string | null): string {
  return [year || null, make || null, model || null].filter(Boolean).join(' ') || '—';
}

export const UsedInventoryAgingCard: React.FC<UsedInventoryAgingCardProps> = ({
  data,
  loading,
  error,
  locationName,
  collapsed = false,
  onToggleCollapse,
}) => {
  const total = data?.totalUsedUnits ?? 0;
  const total60Plus = data?.totalUnits60Plus ?? 0;
  const pct60Plus = data?.pct60Plus ?? 0;
  const avgAge = data?.avgAgeDays ?? 0;
  const oldest = data?.oldestUnit ?? null;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      {/* Header */}
      <div
        className={`px-4 py-3 ${collapsed ? '' : 'border-b border-gray-200'} ${onToggleCollapse ? 'cursor-pointer select-none hover:bg-gray-50 transition-colors' : ''}`}
        onClick={onToggleCollapse}
      >
        <div className="flex items-center gap-3">
          <div className="p-2 bg-emerald-100 rounded-lg">
            <CubeIcon className="h-5 w-5 text-emerald-600" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900">Inventory</h3>
            <p className="text-sm text-gray-500">
              {loading
                ? 'Loading…'
                : data && data.available
                  ? (
                    <>
                      {formatNumber(total)} used units in stock
                      {total > 0 && (
                        <>
                          {' '}· {formatNumber(total60Plus)} ({formatPct(pct60Plus)}) over 60 days
                        </>
                      )}
                      {data.snapshotDate && (
                        <span className="ml-2 text-xs text-gray-400">· as of {formatDate(data.snapshotDate)}</span>
                      )}
                    </>
                  )
                  : 'No inventory snapshot available yet'}
            </p>
          </div>
          {onToggleCollapse && (
            <ChevronDownIcon
              className={`h-5 w-5 text-gray-400 transition-transform duration-200 ${collapsed ? '' : 'rotate-180'}`}
            />
          )}
        </div>
      </div>

      {/* Content */}
      {!collapsed && (
        <div className="p-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 mb-4 text-sm">
              {error}
            </div>
          )}

          {loading ? (
            <div className="py-8 text-center text-sm text-gray-500">Loading inventory aging data…</div>
          ) : !data || !data.available || total === 0 ? (
            <div className="py-8 text-center text-sm text-gray-500">
              No inventory snapshot is available for {locationName} yet. Daily snapshots are written each
              morning at ~5 AM EST and a record will appear here on the next run.
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                    <tr>
                      <th className="px-3 py-2 text-right">Total Units</th>
                      <th className="px-3 py-2 text-right">60+ Days</th>
                      <th className="px-3 py-2 text-right">% 60+</th>
                      <th className="px-3 py-2 text-right">Avg Days</th>
                      <th className="px-3 py-2 text-right">Oldest (Days)</th>
                      <th className="px-3 py-2 text-left">Oldest Unit</th>
                      <th className="px-3 py-2 text-left">Stock #</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-t-2 border-gray-300 bg-gray-50/60">
                      <td className="px-3 py-2 text-right tabular-nums font-bold text-gray-900">
                        {formatNumber(total)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums font-bold text-gray-900">
                        {formatNumber(total60Plus)}
                      </td>
                      <td
                        className={`px-3 py-2 text-right tabular-nums font-bold ${pct60Plus >= 30 ? 'text-red-600' : pct60Plus >= 15 ? 'text-amber-600' : 'text-gray-900'}`}
                      >
                        {formatPct(pct60Plus)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums font-bold text-gray-900">
                        {avgAge.toFixed(1)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums font-bold text-gray-900">
                        {oldest ? formatNumber(oldest.ageDays) : <span className="text-gray-400">—</span>}
                      </td>
                      <td className="px-3 py-2 font-bold text-gray-700">
                        {oldest ? ymmDescription(oldest.year, oldest.make, oldest.model) : <span className="text-gray-400">—</span>}
                      </td>
                      <td className="px-3 py-2 font-bold text-gray-700 font-mono text-xs">
                        {oldest?.stockNumber || <span className="text-gray-400">—</span>}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {data.unitsWithBadReceivedDate > 0 && (
                <div className="mt-3 text-xs text-amber-600">
                  Note: {formatNumber(data.unitsWithBadReceivedDate)} unit
                  {data.unitsWithBadReceivedDate === 1 ? ' has' : 's have'} a `datereceived` earlier than its
                  record creation date — age is measured from the later of the two to avoid inflated ages.
                </div>
              )}

              <div className="mt-3 text-xs text-gray-500">
                Source: DealerBuilt `lystock` (used, available, not deleted). Snapshot taken{' '}
                {formatDate(data.snapshotDate)}. Daily snapshots are saved for trend reporting.
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};
